import React, { useMemo } from "react";
import { Field } from "react-final-form";
import { Autocomplete, TextField } from "@mui/material";
import RffInputWrapper from "../shared/RffInputWrapper";
import normalizeOptions from "../../../utils/finalForm/normalizeOptions";

/**
 * RffSelectAutocompleteField — a type-to-filter single select. Use it wherever
 * the option list is long enough that scanning a dropdown is worse than typing
 * (US states, entity types).
 *
 * Accepts options as plain strings or `{ value, label }`; the stored form value
 * is always the option's `value`.
 */
const RffSelectAutocompleteField = ({
  name,
  label,
  options = [],
  required = false,
  helperText,
  placeholder,
  row = false,
  suppressGrid,
  size,
  disabled,
  alphabetize = false,
}) => {
  const resolvedOptions = useMemo(() => {
    const normalized = normalizeOptions(options);
    if (!alphabetize) return normalized;
    return [...normalized].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [options, alphabetize]);

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
      row={row}
    >
      <Field
        name={name}
        validate={(v) =>
          required && (v == null || v === "") ? "This field is required" : undefined
        }
        render={({ input, meta }) => (
          <Autocomplete
            options={resolvedOptions}
            disabled={disabled}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? resolvedOptions.find((o) => o.value === option)?.label || option
                : option?.label || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.value === (typeof value === "string" ? value : value?.value)
            }
            value={
              resolvedOptions.find((o) => o.value === input.value) || null
            }
            onChange={(_, next) => input.onChange(next ? next.value : undefined)}
            onBlur={input.onBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                error={meta.touched && !!meta.error}
                helperText={meta.touched && meta.error ? meta.error : null}
              />
            )}
          />
        )}
      />
    </RffInputWrapper>
  );
};

export default RffSelectAutocompleteField;
